import {
  ActionIcon,
  Flex,
  FocusTrap,
  Group,
  List,
  Space,
  Switch,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  useDebouncedCallback,
} from '@mantine/hooks';
import {
  modals,
} from '@mantine/modals';
import AddIcon from '@mui/icons-material/Add';
import {
  useForm,
} from '@tanstack/react-form';
import {
  createFileRoute,
  Outlet,
  useNavigate,
} from '@tanstack/react-router';
import React, {
  useMemo,
  useState,
} from 'react';

import {
  SearchInput,
} from '#components/ui/input/SearchInput';
import {
  type ClientWithId,
} from '#features/client/Client.schema';
import {
  NewRequest,
} from '#src/features/scout/NewRequest';
import styles from '#features/scout/DigitalAssistants/ViewRequests/ATOReporter/Requests.module.scss';
import {
  requestsApiResponseSchema,
  type Request,
  type RequestsApiResponse,
} from '#features/scout/Schema/Requests.schema';
import {
  formatDate,
} from '@specia-accountant/shared';
import {
  resolveObject,
} from '@specia-accountant/shared';

interface LoaderDataResult {
  clients: ClientWithId[];
  requests: RequestsApiResponse;
}

export const Route = createFileRoute(
  '/_app/_authenticated/mydigitalworkers/scout/_scoutLayout/requests',
)({
  component: RouteComponent,
  beforeLoad: () => {
    return {
      crumb: 'Requests',
    };
  },
  loader: async (opts): Promise<LoaderDataResult> => {
    const requests: Promise<LoaderDataResult['requests']> = opts.context.queryClient.ensureQueryData({
      queryKey: [
        'reporter',
        'requests',
      ],
    });
    const clients: Promise<LoaderDataResult['clients']> = opts.context.queryClient.ensureQueryData({
      queryKey: [
        'reporter',
        'clients',
      ],
    });
    const result = await resolveObject({
      requests,
      clients,
    });
    result.requests = requestsApiResponseSchema.parse(result.requests).map((request) => {
      const {
        request_status,
        ...rest
      } = request;
      return {
        display_status: request_status === 'success' ? 'Success' : (request_status === 'processing' ? 'Queued' : 'Failed'),
        request_status,
        ...rest,
      };
    })
    return result;
  },
});

function DisplayClientNameList(
  request: Request,
  ref?: React.LegacyRef<HTMLDivElement>,
): React.ReactNode {
  const clients = request.json_data.clients;
  const clientNamesList = (clients?.map(client => client.companyName) ?? ['No clients in request']).sort();
  const maxDisplayClients = 1;
  const numClients = clientNamesList.length;
  const separator = `,`;
  const elipsis = ` ... [${numClients}]`;
  const displayClientsStr = (numClients > maxDisplayClients) ? `${clientNamesList.slice(0, maxDisplayClients).join(separator)}, ${elipsis}` : clientNamesList.join(separator);
  const labelList = clientNamesList?.map((client, clientIndex) => (
    <List.Item
      key={ clientIndex }
    >
      { client }
    </List.Item>
  ));
  if (numClients > maxDisplayClients) {
    return (
      <Tooltip
        ref={ ref }
        id={ `request-${request.id}-clients-tooltip` }
        label={ (<List>{ labelList }</List>) }
        position="top-start"
        arrowPosition="side"
        arrowOffset={ 6 }
        arrowSize={ 12 }
        withArrow
        withinPortal={ false }
      >
        <Text
          size="md"
        >
          { displayClientsStr }
        </Text>
      </Tooltip>
    );
  }
  return displayClientsStr;
}
function RouteComponent(): React.ReactNode {
  const navigate = useNavigate();
  const {
    clients,
    requests: requestData,
  } = Route.useLoaderData();
  const [
    filterSwitchValues,
    setFilterSwitchValues,
  ] = useState<('Multiple' | 'Completed')[]>([]);
  const entryValues = useMemo(() => {
    return requestData.map(entry => [
      ...Object.values(entry ?? {}).filter(item => typeof item === 'string'),
      ...(Object.values(entry?.json_data ?? {}).map((item) => {
        if (item instanceof Date) {
          return formatDate(item);
        }
      }) ?? []),
      ...(entry?.json_data?.clients?.flatMap(client => Object.values(client)) ?? []),
    ].filter(entry => entry ? entry : false),
    ) as string[][];
  }, [requestData]);
  const [
    filteredRequests,
    setFilteredRequests,
  ] = useState(requestData);
  const handleSearch = useDebouncedCallback(async (formData) => {
    const searchTerm = formData.value.searchTerm;
    const filterResult = requestData.filter((_entry: Request, entryIndex: keyof (typeof entryValues)) => {
      const entryProperties = entryValues[entryIndex] as string[];
      console.debug(entryProperties);
      return entryProperties.some((val) => {
        return (
          typeof val === 'string'
          && val
            .toLowerCase()
            .includes(
              searchTerm.toLowerCase(),
            )
        );
      });
    });
    console.debug(
      `Search term ${searchTerm}, matches ${filterResult.length} / ${requestData.length}`,
      requestData,
    );
    setFilteredRequests(filterResult);
  }, 250);
  const {
    Field,
    handleSubmit,
    state: _state,
  } = useForm({
    defaultValues: {
      searchTerm: '',
    },
    onSubmit: async (formData) => {
      handleSearch(formData);
    },
  });
  const tableData = {
    // caption: 'Client reports requested',
    head: [
      // 'ID',
      'CLIENT NAME',
      'PERIOD START',
      'PERIOD END',
      'REQUESTED BY',
      'STATUS',
    ],
    body: filteredRequests.filter(request =>
      (filterSwitchValues.includes('Multiple') && ((request.json_data.clients?.length ?? 0) > 1)) || (!filterSwitchValues.includes('Multiple') && request),
    ).filter(request =>
      !(filterSwitchValues.includes('Completed') && (request.display_status !== 'Success')),
    ).map((request) => {
      return {
        id: request.id,
        columns: [
          // request.id,
          DisplayClientNameList(request),
          formatDate(request.json_data.startDate),
          formatDate(request.json_data.endDate),
          request.requested_by,
          request.display_status,
        ],
      };
    }),
  };
  const tableRows = tableData.body.map((row, rowIndex) => (
    <Table.Tr
      key={ rowIndex }
      className={ styles.tableRow }
      onClick={ () => navigate({
        to: '/mydigitalworkers/scout/reports/$reportId',
        params: {
          reportId: `${row.id}`,
        },
      }) }
    >
      { row.columns.map((cell, cellIndex) => (
        <Table.Td
          key={ cellIndex }
        >
          { cell }
        </Table.Td>
      )) }
    </Table.Tr>
  ));
  const tableHeader = (rowKey: string) => (
    <Table.Tr
      key={ rowKey }
    >
      { tableData.head.map((col, colIndex) => (
        <Table.Td
          key={ colIndex }
        >
          { col }
        </Table.Td>
      )) }
    </Table.Tr>
  );
  return (
    <>
      { /* <Space h="xl" /> */ }
      <Flex
        id="requests-table-search"
        direction="row"
        align="center"
        justify="space-between"
      >
        <form
          id="requests-search-bar"
          onSubmit={ (e) => {
            e.preventDefault();
          // handleSubmit()a
          } }
          onChange={ (e) => {
            e.preventDefault();
            handleSubmit();
          } }
        >
          <Field
            name="searchTerm"
            children={ ({
              state,
              handleChange,
              handleBlur,
            }) => (
              <SearchInput
                data={ [] }
                onChange={ (evt: string): void => handleChange(evt) }
                onBlur={ handleBlur }
                value={ state.value }
              />
            ) }
          />
        </form>
        <Group>
          <Switch.Group
            value={ filterSwitchValues }
            onChange={ (value: ('Completed' | 'Multiple')[]) => setFilterSwitchValues(value) }
          >
            <Group>
              <Switch
                value="Completed"
                color="violet"
                onLabel="Success"
                offLabel="All"
                size="xl"
              />
              <Switch
                value="Multiple"
                color="violet"
                onLabel="Multiple"
                offLabel="Single"
                size="xl"
              />
            </Group>
          </Switch.Group>
          <Space
            w="lg"
          />
          <Tooltip
            id="tooltip-submit"
            label="Create Request"
            position="left"
            arrowPosition="side"
            arrowOffset={ 2 }
            arrowSize={ 12 }
            withArrow
          >
            <ActionIcon
              id="create-new-request"
              variant="gradient"
              size="xl"
              radius="xl"
              aria-label="Create request"
              gradient={ {
                from: 'transparent.1',
                to: 'transparent.2',
                deg: 30,
              } }
              onClick={ () => {
                const newRequestModalId = modals.open({
                  closeOnClickOutside: true,
                  withCloseButton: true,
                  withinPortal: true,
                  title: (
                    <Text
                      c="light.2"
                      size="xl"
                    >
                      New Request
                    </Text>
                  ),
                  children: (
                    <>
                      <FocusTrap.InitialFocus />
                      <NewRequest
                        clients={ clients }
                        close={ () => modals.close(newRequestModalId) }
                      />
                    </>
                  ),
                });
              } }
            >
              <AddIcon />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Flex>
      <Table.ScrollContainer
        className={ styles.tableContainer }
        type="native"
        minWidth={ 500 }
      >
        <Table
          classNames={ {} }
          // data={tableData}
          verticalSpacing="sm"
          horizontalSpacing="lg"
          striped="odd"
          stripedColor="oklch(1 0 0 /0.05)"
          highlightOnHoverColor="primary.9"
          highlightOnHover
          stickyHeader
          // stickyHeaderOffset={0}
          // bd={3}
          // withTableBorder
          tabularNums
        >
          <Table.Thead>{ tableHeader('requests-table-header') }</Table.Thead>
          <Table.Tbody>{ tableRows }</Table.Tbody>
          { /* <Table.Tfoot>{tableHeader("requests-table-footer")}</Table.Tfoot> */ }
        </Table>
      </Table.ScrollContainer>
      { /* <Requests
      requestData={requestData}
      isLoading={false}
    /> */ }
      { /* <Space h="sm" /> */ }
      <Outlet />
      { /* <Space h="lg" /> */ }
    </>
  );
}
